import { Bike, Car, Ticket } from "@ds/icons";
import {
	apiClient,
	Button,
	ButtonGroup,
	CheckboxFormInput,
	DropdownFormInput,
	FormGroup,
	formatter,
	Message,
	parser,
	RadioButtonFormInput,
	TextFormInput,
	useLanguageResource,
} from "@ruter-as/web-components-and-tools";
import { addDays } from "date-fns";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import type { AvailableSubscription } from "../../api/availableSubscription";
import type { SubscriptionType } from "../../api/subscriptionType";
import widgetApi from "../../api/widgetApi";
import getChangeDates from "../../dateUtils";
import { getEnv } from "../../env";
import getSubscriptionText from "../../subscriptionText";
import { getProductPrice } from "../../tools/getProductPrice";
import { useAuthenticatedAuthContext } from "../auth/Auth.component";
import Confirmation, {
	type ConfirmationProps,
} from "../confirmation/Confirmation";
import Conditions from "../inputs/conditions/Conditions";
import ZoneSelector, {
	ZONE_TO_NAME,
} from "../inputs/zone-selector/ZoneSelector";
import ProductPrice from "../price/ProductPrice";
import orderFormLanguageResource from "./lang-resource";
import "./OrderForm.scss";
import type { ProfileId } from "../../types/ProfileId";

export interface FormData {
	phone: string;
	firstName: string;
	lastName: string;
	employeeNumber: string;
	startDate: string;
	zoneFrom: string;
	zoneTo: string;
	profile: string;
	TICKET: boolean;
	JOULE: boolean;
	CAR_POOL: boolean;
}

interface TicketPostContract {
	phone: string;
	phoneCountryCode: string;
	firstName: string;
	lastName: string;
	employeeId: string;
	startDate: string;
	zoneFrom: string;
	zoneTo: string;
	zoneVia: "";
	phoneVerificationCode: string;
	token: string;
	agreementNumber: string;
	profile?: string;
}

const sortAvailableSubscriptions = (
	a: AvailableSubscription,
	b: AvailableSubscription,
): number => {
	const sortOrder: SubscriptionType[] = [
		"TICKET",
		"TRAVEL_SUBSCRIPTION",
		"CAR_POOL",
		"JOULE",
	];
	const sortA = sortOrder.indexOf(a.type);
	const sortB = sortOrder.indexOf(b.type);

	if (sortA > sortB) {
		return 1;
	}
	if (sortA === sortB) {
		return 0;
	}
	return -1;
};

const getIcon = (type: SubscriptionType) => {
	switch (type) {
		case "CAR_POOL":
			return <Car aria-hidden />;
		case "JOULE":
			return <Bike aria-hidden />;
		case "TICKET":
			return <Ticket aria-hidden />;
		case "UNKNOWN":
			return null;
	}
};

const OrderForm: React.FC = () => {
	const navigate = useNavigate();
	const {
		token,
		selectedCompany,
		userData,
		zones,
		tickets,
		subscriptions,
		productTemplate,
	} = useAuthenticatedAuthContext();
	const lang = useLanguageResource(orderFormLanguageResource);
	const [submitting, setSubmitting] = useState(false);
	const startDates = getChangeDates();
	const [showConfirmationMessage, setShowConfirmationMessage] = useState<
		ConfirmationProps | false
	>(false);
	const [formError, setFormError] = useState<false | string>(false);

	const env = getEnv();

	let filteredAvailableSubscriptions = [
		...selectedCompany.availableSubscriptions,
	].sort(sortAvailableSubscriptions);
	if (
		tickets.some(
			(ticket) =>
				(ticket.status === "active" && ticket.expirationDate === null) ||
				ticket.status === "pending",
		)
	) {
		filteredAvailableSubscriptions = filteredAvailableSubscriptions.filter(
			(x) => x.type !== "TICKET" && x.type !== "TRAVEL_SUBSCRIPTION",
		);
	}
	if (
		subscriptions.some(
			(subscription) =>
				subscription.type === "JOULE" &&
				(subscription.state === "ACTIVE" || subscription.state === "PENDING"),
		)
	) {
		filteredAvailableSubscriptions = filteredAvailableSubscriptions.filter(
			(x) => x.type !== "JOULE",
		);
	}
	if (
		subscriptions.some(
			(subscription) =>
				subscription.type === "CAR_POOL" &&
				(subscription.state === "ACTIVE" || subscription.state === "PENDING"),
		)
	) {
		filteredAvailableSubscriptions = filteredAvailableSubscriptions.filter(
			(x) => x.type !== "CAR_POOL",
		);
	}

	const existingSubscription = tickets[0] || subscriptions[0];

	const getProfileOptions = () => {
		const profileOptions: { text: string; value: ProfileId }[] = [];

		for (const product of productTemplate.products) {
			const optionExists = profileOptions.some(
				(x) => x.value === product.profileId,
			);
			if (!optionExists && product.profileDescription) {
				profileOptions.push({
					text: product.profileDescription,
					value: product.profileId,
				});
			}
		}

		return profileOptions;
	};

	const formMethods = useForm<FormData>({
		defaultValues: {
			employeeNumber: existingSubscription?.employeeId || "",
			firstName: existingSubscription?.firstName || "",
			lastName: existingSubscription?.lastName || "",
			startDate: startDates[0],
			zoneFrom: zones[0].name,
			zoneTo: zones[0].name,
			profile: env.hasProfiles ? getProfileOptions()[0].value : undefined,
			TICKET:
				filteredAvailableSubscriptions.some(
					(x) => x.type === "TICKET" || x.type === "TRAVEL_SUBSCRIPTION",
				) && filteredAvailableSubscriptions.length === 1,
			JOULE:
				filteredAvailableSubscriptions.some((x) => x.type === "JOULE") &&
				filteredAvailableSubscriptions.length === 1,
			CAR_POOL:
				filteredAvailableSubscriptions.some((x) => x.type === "CAR_POOL") &&
				filteredAvailableSubscriptions.length === 1,
		},
	});

	const { TICKET, JOULE, CAR_POOL, zoneFrom, zoneTo, profile } =
		formMethods.watch();

	const ticketOwnerFieldName = selectedCompany.ticketOwnerFieldName
		? selectedCompany.ticketOwnerFieldName
		: lang.inputs.employeeNumber;

	const duplicateEmployeeIdLang = lang.errors.duplicateEmployeeId(
		ticketOwnerFieldName.replace(".", ""),
	);
	const quarantineErrorTemplate = lang.errors.quarantineError("$p1$", "$p2$");

	useEffect(() => {
		formMethods.setValue(ZONE_TO_NAME, zoneFrom);
	}, [zoneFrom, formMethods]);

	const submitOrder = async (data: FormData): Promise<void> => {
		setSubmitting(true);
		const body: TicketPostContract = {
			agreementNumber: selectedCompany.agreementId,
			employeeId: data.employeeNumber,
			firstName: data.firstName,
			lastName: data.lastName,
			phone: userData.phone,
			phoneCountryCode: userData.phoneCountryCode,
			startDate: parser.date.fromShortDateString(data.startDate).toISOString(),
			token,
			phoneVerificationCode: userData.phoneVerificationCode,
			zoneFrom: data.zoneFrom,
			zoneTo: data.zoneTo,
			zoneVia: "",
		};

		if (data.profile) {
			body.profile = data.profile;
		}

		let url = "/api/ticket/widget/create";
		if (selectedCompany.widgetPolicy === "request") {
			url = "/api/ticket-request/widget/create";
		}

		if (data.TICKET) {
			const response = await apiClient.post(url, body, (json) => json);

			if (response.error) {
				if (response.type === "HttpError" && response.responseStatus === 409) {
					const tickets = response.responseBody as Array<{
						expirationDate: string;
					}>;
					if (tickets.length > 0 && tickets[0].expirationDate) {
						const expirationDate = new Date(tickets[0].expirationDate);
						const firstStartDate = addDays(expirationDate, 1);
						setFormError(
							quarantineErrorTemplate
								.replace(
									"$p1$",
									formatter.date.toShortDateString(expirationDate),
								)
								.replace(
									"$p2$",
									formatter.date.toShortDateString(firstStartDate),
								),
						);
					} else {
						setFormError(lang.errors.openTicketError);
					}
				} else if (
					response.type === "HttpError" &&
					response.responseStatus === 422
				) {
					setFormError(duplicateEmployeeIdLang);
				} else {
					setShowConfirmationMessage(() => {
						throw response.error;
					});
				}
				setSubmitting(false);
				return;
			}
		}

		if (data.JOULE) {
			const response = await apiClient.request(
				widgetApi.subscriptions.create(
					token,
					userData,
					selectedCompany.agreementId,
					data.employeeNumber,
					data.firstName,
					data.lastName,
					"JOULE",
				),
			);

			if (response.error) {
				setSubmitting(() => {
					throw response.error;
				});
				return;
			}
		}

		if (data.CAR_POOL) {
			const response = await apiClient.request(
				widgetApi.subscriptions.create(
					token,
					userData,
					selectedCompany.agreementId,
					data.employeeNumber,
					data.firstName,
					data.lastName,
					"CAR_POOL",
				),
			);

			if (response.error) {
				setSubmitting(() => {
					throw response.error;
				});
				return;
			}
		}

		setSubmitting(false);
		setShowConfirmationMessage({
			showCarRental: data.CAR_POOL,
			showCompanyTicket: data.TICKET,
			showJoule: data.JOULE,
			carPoolSmsDate: filteredAvailableSubscriptions.some(
				(x) => x.type === "TRAVEL_SUBSCRIPTION",
			)
				? parser.date.fromShortDateString(data.startDate)
				: undefined,
		});
	};

	const getPrice = (): number | "UNKNOWN" => {
		let price = 0;

		if (TICKET) {
			const ticketPrice = getProductPrice(
				productTemplate.products,
				zoneFrom,
				zoneTo,
				zones,
				profile,
			);
			if (ticketPrice === "UNKNOWN") {
				return "UNKNOWN";
			}
			price = ticketPrice;
		}
		if (JOULE) {
			price +=
				filteredAvailableSubscriptions.find((x) => x.type === "JOULE")
					?.pricePerMonth || 0;
		}
		if (CAR_POOL) {
			price +=
				filteredAvailableSubscriptions.find((x) => x.type === "CAR_POOL")
					?.pricePerMonth || 0;
		}

		return price;
	};

	if (showConfirmationMessage) {
		return <Confirmation {...showConfirmationMessage} />;
	}

	const getDescription = () => {
		if (selectedCompany.hasTravelSubscription) {
			return filteredAvailableSubscriptions.find(
				(x) => x.type === "TRAVEL_SUBSCRIPTION",
			)?.description.nb;
		}

		return filteredAvailableSubscriptions.length === 1
			? filteredAvailableSubscriptions[0].description.nb
			: undefined;
	};

	const showSubscriptions =
		filteredAvailableSubscriptions.length > 1 &&
		!selectedCompany.hasTravelSubscription;
	const showFormDetails = JOULE || TICKET || CAR_POOL;
	const subscriptionTitle =
		filteredAvailableSubscriptions.length === 1
			? `Legg til ${getSubscriptionText(filteredAvailableSubscriptions[0].name.nb, selectedCompany.subTypeId === "HOUSING_COOPERATIVE_AGREEMENT")}`
			: "Legg til abonnement";
	const subscriptionDescription = getDescription();
	const disableNamesAndNumber = Boolean(existingSubscription);

	return (
		<div className="widget-order-form" data-test-id="order-page">
			<h1>{subscriptionTitle}</h1>
			{subscriptionDescription && (
				<p data-test-id="subscription-description">{subscriptionDescription}</p>
			)}
			{showSubscriptions && (
				<p>Huk av for det eller de abonnementene du ønsker</p>
			)}

			{filteredAvailableSubscriptions.length === 0 && (
				<div data-test-id="no-available-subscription-message">
					<Message skin="info">
						<p>
							Det er ikke mulig å legge til flere abonnement enn de(n) du
							allerede har.
						</p>
					</Message>
				</div>
			)}

			<FormProvider {...formMethods}>
				<form
					onSubmit={formMethods.handleSubmit(submitOrder)}
					data-test-id="order-form"
					className="order-form"
				>
					{showSubscriptions && (
						<FormGroup style={{ marginTop: "2rem" }}>
							<fieldset
								style={{ margin: 0, border: 0, padding: 0 }}
								data-test-id="services"
							>
								{filteredAvailableSubscriptions.map((sub) => (
									<FormGroup key={sub.type}>
										<CheckboxFormInput
											name={sub.type}
											variant="card"
											startIcon={getIcon(sub.type)}
											label={getSubscriptionText(
												sub.name.nb,
												selectedCompany.subTypeId ===
													"HOUSING_COOPERATIVE_AGREEMENT",
											)}
										></CheckboxFormInput>
									</FormGroup>
								))}
							</fieldset>
						</FormGroup>
					)}

					{showFormDetails && (
						<>
							{TICKET && (
								<DropdownFormInput
									name="startDate"
									label={getSubscriptionText(
										lang.inputs.startDate,
										selectedCompany.subTypeId ===
											"HOUSING_COOPERATIVE_AGREEMENT",
									)}
								>
									{startDates.map((date) => (
										<option key={date} value={date}>
											{date}
										</option>
									))}
								</DropdownFormInput>
							)}

							<TextFormInput
								name="firstName"
								required
								label={lang.inputs.firstName}
								maxLength={50}
								disabled={disableNamesAndNumber}
							/>
							<TextFormInput
								name="lastName"
								required
								label={lang.inputs.lastName}
								maxLength={50}
								disabled={disableNamesAndNumber}
							/>
							<TextFormInput
								name="employeeNumber"
								required
								label={ticketOwnerFieldName}
								maxLength={20}
								disabled={disableNamesAndNumber}
							/>

							{TICKET && (
								<>
									<ZoneSelector />
									{env.hasProfiles && (
										<RadioButtonFormInput
											name="profile"
											label="Reisende"
											options={getProfileOptions()}
										/>
									)}
								</>
							)}

							<ProductPrice price={getPrice()} />

							<Conditions
								showCompanyTicket={TICKET}
								showSubscription={
									JOULE || CAR_POOL || selectedCompany.hasTravelSubscription
								}
								selectedCompany={selectedCompany}
							/>
						</>
					)}

					{formError && (
						<Message
							data-test-id="form-error"
							style={{ marginTop: "2rem" }}
							skin="danger"
						>
							{formError}
						</Message>
					)}

					<div style={{ marginTop: "3rem" }}>
						<ButtonGroup>
							{showFormDetails && (
								<>
									<Button type="submit" skin="primary" loading={submitting}>
										{lang.buttons.submitOrder}
									</Button>
									<Button
										type="button"
										skin="secondary"
										onClick={() => navigate("/abonnement")}
									>
										{lang.buttons.cancel}
									</Button>
								</>
							)}
						</ButtonGroup>
					</div>
				</form>
			</FormProvider>
		</div>
	);
};

export default OrderForm;
